import React, { useEffect, useState } from "react";
import "./UserMaintenance.css";
import { useDispatch, useSelector } from "react-redux";
import {
  getPermissionsData,
  addPermissionsData,
  updatePermissionsData,
  deletePermissionsData,
} from "../Redux/UserMaintenance";

import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Snackbar,
  Select,
  MenuItem,
  OutlinedInput,
} from "@mui/material";

import { Box } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloseRounded,
} from "@mui/icons-material";

const blankPermission = {
  id: null,
  empId: "",
  role: "",
  permissions: "",
  createdAt: "",
  updatedAt: "",
};

const blankSnackbar = {
  open: false,
  message: "",
  severity: "",
};

function UserMaintenance() {
  const dispatch = useDispatch();
  const { permissionsData } = useSelector((state) => state.userMaintenance);

  const [editPermission, setEditPermission] = useState(blankPermission);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [snackbarParams, setSnackbarParams] = useState(blankSnackbar);

  useEffect(() => {
    dispatch(getPermissionsData());
  }, [dispatch]);

  const columns = [
    { id: "empId", label: "Employee ID" },
    { id: "role", label: "Role" },
    { id: "permissions", label: "Course Access" },
    { id: "createdAt", label: "Created On" },
    { id: "updatedAt", label: "Updated On" },
    { id: "actions", label: "Actions" },
  ];

  const formatDate = (dateInput) => {
    if (!dateInput) return "";
    const d = new Date(dateInput);
    if (isNaN(d)) return "";
    return d.toLocaleDateString("en-GB");
  };

  const formatTimeFromDate = (dateInput) => {
    if (!dateInput) return "";
    const d = new Date(dateInput);
    if (isNaN(d)) return "";

    return d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const handleOpenEditModal = (data) => {
    setEditPermission(data);
    setOpenEditModal(true);
  };

  const handleDeleteSubmit = async () => {
    try {
      await dispatch(deletePermissionsData(editPermission));
      setSnackbarParams({
        open: true,
        message: "Access Deleted Successfully",
        severity: "success",
      });
      setOpenDeleteModal(false);
      setEditPermission(blankPermission);
    } catch {
      setSnackbarParams({
        open: true,
        message: "Delete Failed",
        severity: "error",
      });
    }
  };

  const PermissionEditModal = () => {
    const isNewPermission = editPermission.id === null;
    const theme = useTheme();

    const [localPermission, setLocalPermission] = useState({
      ...editPermission,
      permissions: editPermission?.permissions?.split(",") || [],
    });

    const handleSubmit = async () => {
      const payload = {
        ...localPermission,
        permissions: localPermission.permissions.join(","),
      };

      try {
        if (payload.id) {
          payload.updatedAt = new Date().toISOString();
          await dispatch(updatePermissionsData(payload));
        } else {
          payload.createdAt = new Date().toISOString();
          payload.updatedAt = new Date().toISOString();
          await dispatch(addPermissionsData(payload));
        }

        setSnackbarParams({
          open: true,
          message: "Saved Successfully",
          severity: "success",
        });
        setOpenEditModal(false);
      } catch {
        setSnackbarParams({
          open: true,
          message: "Save Failed",
          severity: "error",
        });
      }
    };

    const permissionsList = [
      "RPA",
      "AWS",
      "Azure",
      "GCP",
      "Java",
      "Data Analytics",
      "Mainframe",
      "Artificial Intelligence",
      "Oracle",
      "SAP",
      "SFDC",
      "Adobe",
      "NGPL",
      "Low Code No Code",
      "EAI",
      "PEGA",
      "QE",
      "Design & Digital Products",
      "Adobe Magneto",
      "Composable Commerce",
      "SAP Commerce",
      "Shopify",
      "Salesforce B2C Commerce Cloud",
      "VTEX Commerce",
      "Delivery",
      "Infrastructure Engineering",
      "Security",
      "DevOps & Site Reliability Engineering",
      "Delivery Automation",
      "Business Innovation & Human skills",
      "Sustainability",
      "Language & Culture",
    ];

    return (
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between">
            <Box>{isNewPermission ? "Add" : "Update"} Access</Box>
            <IconButton onClick={() => setOpenEditModal(false)}>
              <CloseRounded />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <InputLabel>Employee ID</InputLabel>
            <TextField
              value={localPermission.empId}
              onChange={(e) =>
                setLocalPermission({
                  ...localPermission,
                  empId: e.target.value,
                })
              }
            />

            <InputLabel>Role</InputLabel>
            <Select
              value={localPermission.role}
              onChange={(e) =>
                setLocalPermission({
                  ...localPermission,
                  role: e.target.value,
                })
              }
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="member">Member</MenuItem>
            </Select>

            <InputLabel>Course Access</InputLabel>
            <Select
              multiple
              value={localPermission.permissions}
              input={<OutlinedInput />}
              onChange={(e) =>
                setLocalPermission({
                  ...localPermission,
                  permissions: e.target.value,
                })
              }
            >
              {permissionsList.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={handleSubmit}>
            {isNewPermission ? "Add" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const DeleteDialog = () => (
    <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
      <DialogTitle>Confirm Delete</DialogTitle>
      <DialogContent>Delete access for {editPermission.empId}?</DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenDeleteModal(false)}>Cancel</Button>
        <Button color="error" onClick={handleDeleteSubmit}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <Box className="um-container">
        <Box className="um-card">
          <Box className="um-header">
            <h5 className="um-title">
              <b>Course Updation Access</b>
            </h5>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              className="um-add-btn"
              onClick={() => {
                setEditPermission(blankPermission);
                setOpenEditModal(true);
              }}
            >
              New Access
            </Button>
          </Box>

          <TableContainer component={Paper} className="um-table-container">
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.id} className="um-table-header">
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {permissionsData?.map((row) => (
                  <TableRow key={row.id} className="um-table-row">
                    {columns.map((col) => {
                      if (col.id === "permissions") {
                        return (
                          <TableCell key={col.id}>
                            <Stack direction="row" flexWrap="wrap">
                              {row.permissions?.split(",").map((p, i) => (
                                <Chip key={i} label={p} className="um-chip" />
                              ))}
                            </Stack>
                          </TableCell>
                        );
                      }

                      if (col.id === "createdAt" || col.id === "updatedAt") {
                        return (
                          <TableCell key={col.id}>
                            <Tooltip title={row[col.id]}>
                              <span>
                                {formatDate(row[col.id])}{" "}
                                {formatTimeFromDate(row[col.id])}
                              </span>
                            </Tooltip>
                          </TableCell>
                        );
                      }

                      if (col.id === "actions") {
                        return (
                          <TableCell key={col.id}>
                            <IconButton
                              color="primary"
                              onClick={() => handleOpenEditModal(row)}
                            >
                              <EditIcon />
                            </IconButton>

                            <IconButton
                              color="error"
                              onClick={() => {
                                setEditPermission(row);
                                setOpenDeleteModal(true);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        );
                      }

                      return <TableCell key={col.id}>{row[col.id]}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <PermissionEditModal />
      <DeleteDialog />

      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        open={snackbarParams.open}
        autoHideDuration={3000}
        onClose={() =>
          setSnackbarParams({
            ...snackbarParams,
            open: false,
          })
        }
      >
        <Alert severity={snackbarParams.severity} variant="filled">
          {snackbarParams.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default UserMaintenance;
